import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Zap, ChevronRight } from "lucide-react";
import { ref, onValue, update } from "../../../Config/matuRtdb";
import { database } from "../../../Config/firebase";
import MatchCard from "./MatchCard";
import BetModal from "./BetModal";
import FloatingBetButton from "./FloatingBetButton";

export default function FeaturedMatches() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countdowns, setCountdowns] = useState({});
  const [selectedBets, setSelectedBets] = useState([]);
  const [betAmount, setBetAmount] = useState("");
  const [showBetModal, setShowBetModal] = useState(false);
  const [selectedButtonsMap, setSelectedButtonsMap] = useState({});

  useEffect(() => {
    // Reference to the matches collection in Realtime Database
    const matchesRef = ref(database, "/App/betting/match");

    // Use onValue to listen for real-time changes
    const unsubscribe = onValue(
      matchesRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const matchesData = [];
          snapshot.forEach((childSnapshot) => {
            const match = childSnapshot.val();
            match.id = childSnapshot.key;

            // Only include matches where isLive is false
            if (match.isLive === false) {
              matchesData.push(match);
            }
          });

          setMatches(matchesData);
          setLoading(false);

          // Set up countdowns and check if any matches should be updated to isLive=true
          updateCountdowns(matchesData);
          checkMatchesStartTime(matchesData);
        } else {
          setMatches([]);
          setLoading(false);
        }
      },
      (error) => {
        console.error("Error fetching matches:", error);
        setLoading(false);
      }
    );

    // Set up interval to update countdowns every second
    const intervalId = setInterval(() => {
      updateCountdowns(matches);
    }, 1000);

    // Clean up listeners and intervals when component unmounts
    return () => {
      unsubscribe();
      clearInterval(intervalId);
    };
  }, [matches]);

  // Check if a match should start based on date and time
  const checkMatchesStartTime = (matchesData) => {
    const currentDate = new Date();
    const formattedCurrentDate = `${String(currentDate.getDate()).padStart(
      2,
      "0"
    )}/${String(currentDate.getMonth() + 1).padStart(
      2,
      "0"
    )}/${currentDate.getFullYear()}`;
    const currentHour = `${String(currentDate.getHours()).padStart(
      2,
      "0"
    )}:${String(currentDate.getMinutes()).padStart(2, "0")}`;

    matchesData.forEach((match) => {
      if (match.date === formattedCurrentDate && match.hours <= currentHour) {
        // Update the match to isLive=true in Realtime Database
        const matchRef = ref(database, `/App/betting/match/${match.id}`);
        update(matchRef, {
          isLive: true,
        })
          .then(() => {
            console.log(
              `Match ${match.homeTeam.name} vs ${match.awayTeam.name} updated to live status`
            );
          })
          .catch((error) => {
            console.error("Error updating match status:", error);
          });
      }
    });
  };

  // Calculate and update countdowns for all matches
  const updateCountdowns = (matchesData) => {
    const newCountdowns = {};
    const currentDate = new Date();

    matchesData.forEach((match) => {
      // Parse match date (DD/MM/YYYY)
      const [day, month, year] = match.date.split("/");
      // Parse match hours (HH:MM)
      const [hours, minutes] = match.hours.split(":");

      // Create date object for match start time
      const matchDate = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hours),
        parseInt(minutes)
      );

      // Calculate time difference in milliseconds
      const diffMs = matchDate - currentDate;

      if (diffMs > 0) {
        // Convert to minutes
        const diffMinutes = Math.floor(diffMs / (1000 * 60));

        // If less than 3 minutes remaining, show countdown
        if (diffMinutes < 3) {
          const diffSeconds = Math.floor(diffMs / 1000);
          const minutes = Math.floor(diffSeconds / 60);
          const seconds = diffSeconds % 60;

          newCountdowns[match.id] = {
            showCountdown: true,
            minutes,
            seconds: seconds < 10 ? `0${seconds}` : seconds,
          };
        } else {
          newCountdowns[match.id] = {
            showCountdown: false,
          };
        }
      } else {
        // Match should be live already
        const matchRef = ref(database, `/App/betting/match/${match.id}`);
        update(matchRef, {
          isLive: true,
        }).catch((error) => {
          console.error("Error updating match status:", error);
        });
      }
    });

    setCountdowns(newCountdowns);
  };

  // Handle click on a bet option
  const handleBetClick = (match, betType, odd) => {
    // Check if this match already has a selected bet
    const existingBetIndex = selectedBets.findIndex(
      (bet) => bet.matchId === match.id
    );

    // Toggle selection if clicking on the same bet option
    if (
      existingBetIndex !== -1 &&
      selectedBets[existingBetIndex].type === betType
    ) {
      // Remove the bet
      const newSelectedBets = [...selectedBets];
      newSelectedBets.splice(existingBetIndex, 1);
      setSelectedBets(newSelectedBets);

      // Update selection map
      setSelectedButtonsMap((prev) => {
        const updated = { ...prev };
        delete updated[match.id];
        return updated;
      });
      return;
    }

    // Create new bet object
    const newBet = {
      matchId: match.id,
      homeTeam: match.homeTeam.name,
      awayTeam: match.awayTeam.name,
      type: betType,
      odd: odd,
      matchDate: match.date,
      matchHours: match.hours,
    };

    // Update selectedBets array
    let updatedBets;
    if (existingBetIndex !== -1) {
      // Replace existing bet for this match
      updatedBets = [...selectedBets];
      updatedBets[existingBetIndex] = newBet;
    } else {
      // Add new bet
      updatedBets = [...selectedBets, newBet];
    }

    setSelectedBets(updatedBets);

    // Update selected buttons map for UI highlighting
    setSelectedButtonsMap((prev) => ({
      ...prev,
      [match.id]: betType,
    }));
  };

  // Remove a bet from the selection
  const removeBet = (matchId) => {
    setSelectedBets((prev) => prev.filter((bet) => bet.matchId !== matchId));

    // Update selection map
    setSelectedButtonsMap((prev) => {
      const updated = { ...prev };
      delete updated[matchId];
      return updated;
    });
  };

  // Calculate combined odds
  const calculateCombinedOdds = () => {
    if (selectedBets.length === 0) return 0;
    return selectedBets
      .reduce((acc, bet) => acc * parseFloat(bet.odd), 1)
      .toFixed(2);
  };

  // Handle bet submission
  const handleSubmitBet = () => {
    if (!betAmount || isNaN(betAmount) || parseFloat(betAmount) <= 0) {
      alert("Por favor ingresa un monto válido");
      return;
    }

    alert(`Apuesta combinada realizada por $${betAmount}`);
    setShowBetModal(false);
    setBetAmount("");
    setSelectedBets([]);
    setSelectedButtonsMap({});
  };

  // Open the betting modal
  const openBetModal = () => {
    if (selectedBets.length === 0) {
      alert("Por favor selecciona al menos una apuesta");
      return;
    }
    setShowBetModal(true);
  };

  // Format the date to show "Hoy" if applicable
  const formatMatchDate = (dateStr, hours) => {
    const today = new Date();
    const formattedToday = `${String(today.getDate()).padStart(
      2,
      "0"
    )}/${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`;

    if (dateStr === formattedToday) {
      return `Hoy ${hours}`;
    }

    // Convert from DD/MM/YYYY to a more readable format
    const [day, month, year] = dateStr.split("/");
    const monthNames = [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ];
    return `${day} ${monthNames[parseInt(month) - 1]} • ${hours}`;
  };

  return (
    <div>
      <motion.div
        className="mb-6 pb-16" // Added padding at bottom for floating button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <Zap className="w-5 h-5 text-yellow-500 mr-2" />
            <h2 className="text-white font-medium">Partidos Destacados</h2>
          </div>
          <Link
            to="/App/betting/match"
            className="flex items-center text-gray-400 hover:text-white cursor-pointer"
          >
            <span className="text-xs mr-1">Ver todos</span>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
          </div>
        ) : (
          <AnimatePresence>
            <div className="space-y-3">
              {matches.length > 0 ? (
                matches.map((match) => (
                  <MatchCard
                    key={match.id}
                    match={match}
                    countdown={countdowns[match.id]}
                    formatMatchDate={formatMatchDate}
                    selectedButton={selectedButtonsMap[match.id]}
                    onBetClick={handleBetClick}
                  />
                ))
              ) : (
                <motion.div
                  className="bg-gray-800 rounded-lg p-4 text-center text-gray-400"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  No hay partidos próximos disponibles
                </motion.div>
              )}
            </div>
          </AnimatePresence>
        )}
      </motion.div>

      {/* Combined Betting Modal */}
      <BetModal
        show={showBetModal}
        onClose={() => setShowBetModal(false)}
        selectedBets={selectedBets}
        removeBet={removeBet}
        calculateCombinedOdds={calculateCombinedOdds}
        betAmount={betAmount}
        setBetAmount={setBetAmount}
        onSubmit={handleSubmitBet}
      />

      {/* Floating Action Button for Combined Bets */}
      <FloatingBetButton
        selectedBetsCount={selectedBets.length}
        onOpen={openBetModal}
      />
    </div>
  );
}
